<template>
  <v-app :class="appClasses" :dir="isRtl ? 'rtl': 'ltr'" :data-angular-loaded="isAngularLoaded ? 'true' : 'false'" id="app">
    <component :is="'style'">
      :root {
        --brand-color: {{primaryColor}};
        --brand-color-rgb: {{brandColorAsRgb}};
        --brand-color-secondary: {{secondaryColor}};
        --brand-color-secondary-rgb: {{secondaryColorAsRgb}};
        --brand-text-color: {{textColor}};
        --floating-mobile-bottom-bar-height: 52px;
        --floating-mobile-bottom-bar-gap: {{mobileBottomBarGap}};
        --floating-mobile-bottom-bar-bottom: calc(env(safe-area-inset-bottom, 0px) + var(--floating-mobile-bottom-bar-gap));
        --edge-of-bottom-navigation: calc(var(--floating-mobile-bottom-bar-bottom) + var(--floating-mobile-bottom-bar-height));
        --floating-mobile-bottom-bar-offset: var(--floating-mobile-content-inset);
        --floating-mobile-bottom-tab-width: 64px;
        --floating-mobile-content-inset: calc(var(--edge-of-bottom-navigation) + var(--size-value3, 12px));
        --floating-mobile-fab-bottom: calc(var(--edge-of-bottom-navigation) + var(--size-value3, 12px));
      }
    </component>
    <SystemNotificationsContainer :class="{'blurred': blurredLayout}" v-if="!isMobile"/>
    <VcLayout class="app-layout">
      <VcOverlay
        class="pov-overlay"
        :color="isVueFullscreen ? 'black' : 'transparent'"
        :value="isOverlayDisplayed || isVueFullscreen"
        @click.native="onOverlayClick"
      />
      <AngularDelegator v-if="isInitComplete"/>
      <VcLayout :class="{'blurred': blurredLayout}" class="pov-layout" column>
        <!-- New Platform Navigation - Desktop top bar -->
        <MasterTopBar v-if="isInitComplete && hasPlatformNavigation && !isMobile"
                      class="master-top-bar-container pointer-events-all"/>

        <MobileGlassyNavigation v-if="showPhase1MobileNav" />

        <VcLayout class="main-content-layout">
          <MenuContainer class="menu-container pointer-events-all"
                         :class="{'wide-menu-container': !isMobile, 'menu-container--mobile': isMobile}"
                         :is-loading="isLayoutLoading"
                         :is-actions-disabled="isLayoutActionsDisabled"/>
          <VcLayout column class="content-column">
            <AiGeneratorAngularAdapter v-if="hasAiGeneratorFf"/>
            <TopBarContainer
                             v-if="shouldShowLegacyTopBar"
                             class="top-bar pointer-events-all"
                             :class="{'desktop': !isMobile}"
                             :is-loading="isLayoutLoading"
                             :is-actions-disabled="isLayoutActionsDisabled"
                             :bizaiAvailable="bizaiAvailable">
            </TopBarContainer>
            <BizAI v-if="bizaiAvailable && !isMobile && isBrandingLoaded"></BizAI>
            <div class="pov-content-container">
              <AiChatPanel v-if="hasAiChat" />
              <router-view class="content-area"
                           :class="{'pointer-events-none': isAngularDisplayed,
                           'pov-content': !isAngularDisplayed,
                           'mobile-display': isMobile,
                           'show-bottom-nav': isMobile && showBottomNav,
                           'mobile-platform-home-content': isMobilePlatformHomeRoute}"/>
            </div>
          </VcLayout>
        </VcLayout>
        <ToastContainer/>
        <CommonModals/>
        <SessionTimoutModal v-if="isSessionPromptVisible"/>
        <SidePane v-if="hasSidePaneFF && isAngularLoaded"/>
        <MobileBottomSheet v-if="isMobile"/>
        <Transition>
          <MainLoader v-if="!featureFlagsLoaded && !isBrandingLoaded"/>
        </Transition>
      </VcLayout>
      <VcLayout v-show="displayedApp && !isMobile || (isMobile && !shouldShowMenu)"
                class="embeddedAppContainer"
                :class="{
                  mobileMenuDisplayed: isMobile && shouldShowMenu,
                  noAppDisplayed: displayedApp === undefined,
                  'show-bottom-nav': isMobile && showBottomNav,
                }">
        <EmbeddedAppDisplay/>
      </VcLayout>
    </VcLayout>
  </v-app>
</template>

<script>

import { mapActions, mapGetters } from 'vuex';
import TopBarContainer from '@/modules/layout/topBar/components/TopBarContainer.vue';
import MasterTopBar from '@/modules/layout/platformNavigation/components/MasterTopBar.vue';
import MenuContainer from '@/modules/layout/mainMenu/components/MenuContainer.vue';
import AngularDelegator from '@/modules/angularDelegator/components/AngularDelegator.vue';
import colorUtil from '@vcita/design-system/utils/colorUtil';
import {
  stashPendingPushParamsFromUrl,
  reportPushNotificationClickFromUrl,
  redirectPushClickFromUrl,
} from '@/modules/notificationCenter/utils/pushNotificationClick';
import initAnalytics from '@/plugins/analytics/initAnalytics';
// Note: Pendo initialization is handled by Angular via setPendoData
import ToastContainer from '@/modules/layout/toast/components/ToastContainer.vue';
import { isMobileLayout } from '@/utils/mobile-detection';
import outgoingPostMessageUtils from '@/utils/eventCommunicationUtils/outgoingPostMessageUtils';
import faviconUtil from '@/utils/faviconUtil';
import SidePane from '@/modules/sidePane/components/SidePane.vue';
import MobileBottomSheet from '@/modules/layout/mobileBottomSheet/components/MobileBottomSheet.vue';
import SystemNotificationsContainer
  from '@/modules/layout/systemNotifications/components/SystemNotificationsContainer.vue';
import SessionTimoutModal
  from '@/modules/layout/sessionTimeoutModal/components/SessionTimoutModal.vue';
import BizAI from '@/modules/bizai/components/BizAI.vue';
import MainLoader from '@/modules/layout/mainLoader/MainLoader.vue';
import CommonModals from '@/modules/layout/commonModals/components/CommonModals.vue';
import AiGeneratorAngularAdapter
  from '@/modules/aiContentGenerator/components/AiGeneratorAngularAdapter.vue';
import EmbeddedAppDisplay from '@/modules/app/views/EmbeddedAppsDisplay.vue';
import MobileGlassyNavigation from '@/modules/layout/platformNavigation/components/mobile/MobileGlassyNavigation.vue';
import MOBILE_PLATFORM_ROUTE_NAMES from '@/modules/layout/platformNavigation/utils/mobilePlatformRoutes';
import communicationSdk from '@/utils/partnerSdk/communicationSdk';
import appMarketNavigationUtils from '@/modules/appMarket/utils/appMarketNavigationUtils';
import { APP_MARKET_FEATURE_FLAG } from '@/modules/appMarket/constants/appMarketConstants';
import handleNavigation, { initNavigationUtil } from '@/utils/navigationUtils';
import getInitialRouteQuery from '@/utils/getInitialRouteQuery/getInitialRouteQuery';
import aiChatService from '@/modules/aiChat/services/aiChatService';
import { installHelpLinkCatcher, syncHelpLinkVisibility } from '@/modules/help/services/helpEntryService';
import mobileUtils from '@/utils/appUtils/mobileUtils';

const TRANSPARENT_BOTTOM_SYSTEM_BAR = 'TransparentBottomSystemBar';
const MOBILE_LAYOUT_METRICS_UPDATED = 'mobile-layout-metrics-updated';

export default {
  name: 'MainApp',
  components: {
    EmbeddedAppDisplay,
    CommonModals,
    AiGeneratorAngularAdapter,
    MainLoader,
    MobileBottomSheet,
    ToastContainer,
    TopBarContainer,
    MasterTopBar,
    MenuContainer,
    AngularDelegator,
    SidePane,
    SystemNotificationsContainer,
    SessionTimoutModal,
    BizAI,
    MobileGlassyNavigation,
    AiChatPanel: () => import('@/modules/aiChat/components/AiChatPanel.vue'),
  },
  data() {
    return {
      didNavigateToDefaultPage: false,
      isTransparentBottomSystemBarSupported: mobileUtils.isFeatureSupported(TRANSPARENT_BOTTOM_SYSTEM_BAR),
      removeMobileFeatureSupportListener: null,
    };
  },
  async created() {
    // Must run before getToken() below - see pushNotificationClick.md.
    stashPendingPushParamsFromUrl();

    // Initialize navigation utilities to avoid circular dependency
    // eslint-disable-next-line store/no-direct-store-access
    initNavigationUtil(this.$router, this.$store);
    installHelpLinkCatcher();
    this.listenToMobileAppFeatures();

    //  Check First if Authenticated
    await this.getToken();
    if (!this.isAuthenticated) {
      await this.redirectToLogin();
      return;
    }

    // Must run before setIsInitComplete below, so an external handoff doesn't flash POV's own
    // UI in first - see pushNotificationClick.md.
    redirectPushClickFromUrl();

    this.setIsInitComplete(true);
    this.setIsMobile(isMobileLayout());

    reportPushNotificationClickFromUrl();

    //  INIT DATA FOR APP
    await Promise.all([
      this.fetchBranding(),
      this.fetchFeatureFlags(),
      this.fetchAttributes(),
      this.fetchPermissions(),
      this.fetchStaffData(),
    ]);
    syncHelpLinkVisibility();
    this.prewarmAiChatBusinessContext();

    this.handleWizards();
    this.initExtendSession();
    this.forceWLDomain();

    if (this.$featureFlagsLoaded()) {
      this.checkLayoutActionsDisabled();
      this.setFavicon();
    }
    this.setIsRtl(this.$i18n.locale === 'he');

    this.aggregateMenuItemsForStaff();
    setTimeout(this.initExternalScripts);

    this.handleQueryParams();
    this.setIsInitialDataLoaded(true);

    initAnalytics(this);
  },
  mounted() {
    this.addEssentialI18nModule('common-pov');
    setTimeout(this.calcTime, 1000);
    this.updatePrimaryThemeColor(this.primaryColor);
    this.updateSecondaryThemeColor(this.secondaryColor);
    this.updateTextThemeColor(this.textColor);
    this.syncPlatformMenuBodyClass();
    this.syncMobileLayoutMetrics();
  },
  beforeDestroy() {
    this.removeMobileFeatureSupportListener?.();
  },
  computed: {
    ...mapGetters('CommonStore', ['isMobile', 'isMobileApp', 'isInitComplete', 'isRtl']),
    ...mapGetters('AuthStore', ['isAuthenticated', 'isSessionPromptVisible']),
    ...mapGetters('MainMenuStore', ['shouldShowMenu', 'isMenuTranslationLoading', 'allMenuItemsLoaded']),
    ...mapGetters('AngularStore', ['isAngularLoaded', 'isAngularDisplayed', 'isOverlayDisplayed', 'isVueFullscreen', 'blurredLayout']),
    ...mapGetters('BusinessStore', ['hasFeature', 'primaryColor', 'secondaryColor', 'textColor', 'featureFlagsLoaded', 'isBrandingLoaded']),
    ...mapGetters('LayoutBottomNavStore', ['showBottomNav']),
    ...mapGetters('LayoutStore', ['isLayoutActionsDisabled', 'formattedDocumentTitle']),
    ...mapGetters('StaffStore', ['staffDataLoaded', 'staffUid', 'staffData', 'defaultPage']),
    ...mapGetters('AppsStore/EmbeddedAppStore', ['displayedApp']),
    isMobile() {
      return isMobileLayout() || this.isMobileApp;
    },
    hasSidePaneFF() {
      return this.hasFeature('side_pane');
    },
    hasAiGeneratorFf() {
      return this.hasFeature('bizai_content_generator_enabled');
    },
    bizaiAvailable() {
      return this.hasFeature('bizai_chat');
    },
    hasPlatformNavigation() {
      return this.hasFeature('rollout.platform_administration.platform_navigation');
    },
    hasAiChat() {
      return this.hasFeature('pkg.ai.bizai_chat');
    },
    hasMobileNavigationRedesign() {
      return this.hasFeature('rollout.platform_administration.mobile_glassy_nav_layout');
    },
    showPhase1MobileNav() {
      return this.isInitComplete && this.hasMobileNavigationRedesign && this.isMobile;
    },
    isDashboardRoute() {
      return this.$route.name === 'dashboard';
    },
    isMobilePlatformHomeRoute() {
      return this.$route.name === MOBILE_PLATFORM_ROUTE_NAMES.HOME;
    },
    showGlassyTopBar() {
      return this.showPhase1MobileNav
        && (this.shouldShowMenu || this.isDashboardRoute || this.isMobilePlatformHomeRoute);
    },
    shouldShowLegacyTopBar() {
      if (!this.isInitComplete) return false;
      if (this.isMobile && this.showGlassyTopBar) return false;
      if (!this.isMobile && this.hasPlatformNavigation) return false;
      return true;
    },
    appClasses() {
      return {
        rtl: this.isRtl,
        'platform-menu': this.hasMobileNavigationRedesign,
        'compact-view': this.showPhase1MobileNav,
      };
    },
    mobileBottomBarGap() {
      if (!this.isMobileApp || !this.isTransparentBottomSystemBarSupported) {
        return 'var(--size-value3, 12px)';
      }

      return '0px';
    },
    mobileLayoutMetricExpressions() {
      return {
        '--floating-mobile-bottom-bar-gap': this.mobileBottomBarGap,
        '--floating-mobile-bottom-bar-height': '52px',
        '--floating-mobile-bottom-bar-bottom': 'calc(env(safe-area-inset-bottom, 0px) + var(--floating-mobile-bottom-bar-gap))',
        '--edge-of-bottom-navigation': 'calc(var(--floating-mobile-bottom-bar-bottom) + var(--floating-mobile-bottom-bar-height))',
        '--floating-mobile-content-inset': 'calc(var(--edge-of-bottom-navigation) + var(--size-value3, 12px))',
        '--floating-mobile-fab-bottom': 'calc(var(--edge-of-bottom-navigation) + var(--size-value3, 12px))',
        '--floating-mobile-menu-spacer': '64px',
      };
    },
    isLayoutLoading() {
      return !this.staffDataLoaded || this.isMenuTranslationLoading || !this.allMenuItemsLoaded;
    },
    brandColorAsRgb() {
      return this.hexToRgbChannels(this.primaryColor);
    },
    secondaryColorAsRgb() {
      return this.hexToRgbChannels(this.secondaryColor);
    },
  },
  methods: {
    ...mapActions('CommonStore', ['setIsInitComplete', 'setIsMobile', 'setIsRtl']),
    ...mapActions('AngularStore', ['setIsOverlayDisplay']),
    ...mapActions('BusinessStore', ['fetchBranding', 'fetchFeatureFlags', 'fetchAttributes']),
    ...mapActions('AuthStore', ['redirectToLogin', 'getToken', 'forceWLDomain', 'initExtendSession']),
    ...mapActions('StaffStore', ['fetchPermissions', 'fetchStaffData']),
    ...mapActions('MainMenuStore', ['aggregateMenuItemsForStaff']),
    ...mapActions('SidePaneStore', { openSidePane: 'newPane' }),
    ...mapActions(['setIsInitialDataLoaded']),
    ...mapActions('LayoutStore', ['checkLayoutActionsDisabled']),
    onOverlayClick() {
      // Notify any registered listeners (e.g. search, notifications) so they can close; then hide overlay
      this.$root.$emit('overlay-clicked');
      this.setIsOverlayDisplay(false);
    },
    prewarmAiChatBusinessContext() {
      if (this.hasAiChat) aiChatService.prewarmBusinessContext();
    },
    syncPlatformMenuBodyClass() {
      document.body.classList.toggle('platform-menu', this.hasMobileNavigationRedesign);
    },
    listenToMobileAppFeatures() {
      this.removeMobileFeatureSupportListener = mobileUtils.onFeatureSupported(this.onMobileAppFeatureSupported);
    },
    onMobileAppFeatureSupported(featureName) {
      if (featureName === TRANSPARENT_BOTTOM_SYSTEM_BAR) {
        this.isTransparentBottomSystemBarSupported = true;
        this.$nextTick(() => this.syncMobileLayoutMetrics());
      }
    },
    syncMobileLayoutMetrics() {
      this.postMessageToAngular(MOBILE_LAYOUT_METRICS_UPDATED, this.resolvedMobileLayoutMetrics());
    },
    resolvedMobileLayoutMetrics() {
      return Object.entries(this.mobileLayoutMetricExpressions).reduce((metrics, [variableName, value]) => ({
        ...metrics,
        [variableName]: this.resolveCssLength(value),
      }), {});
    },
    resolveCssLength(value) {
      if (!document.body) return value;

      const probe = document.createElement('div');
      probe.style.position = 'fixed';
      probe.style.visibility = 'hidden';
      probe.style.pointerEvents = 'none';
      probe.style.height = value;

      document.body.appendChild(probe);
      const resolvedValue = window.getComputedStyle(probe).height;
      probe.remove();

      return resolvedValue || value;
    },
    postMessageToAngular(event, data) {
      const iframeWindow = document.getElementById('angular-iframe')?.contentWindow;
      if (!iframeWindow) return;

      iframeWindow.postMessage({
        event: 'on-message',
        payload: {
          source: 'pov-wrapper',
          event,
          data,
        },
      }, '*');
    },
    hexToRgbChannels(color) {
      const hex = color?.trim().replace('#', '').toLowerCase();
      if (!/^[0-9a-f]{6}$/.test(hex)) {
        return '0 0 0';
      }

      return hex
        .match(/.{2}/g)
        .map((pair) => parseInt(pair, 16))
        .join(' ');
    },
    ...mapActions('ExternalScriptsStore', ['setFullstoryData', 'initExternalScripts']),
    calcTime() {
      const style = 'color: #0093b8;font-size: 40px;text-shadow: 1px 1px 5px #c2e5ee;filter: dropshadow(color=#c2e5ee, offx=1, offy=1)';
      console.log('%cpov', style);
      const entries = performance.getEntriesByType('navigation');
      entries.forEach((entry) => {
        console.log(`POV load time: ${(entry.domContentLoadedEventEnd / 1000).toFixed(2)}sec`);
      });
    },
    updatePrimaryThemeColor(color) {
      this.$vuetify.theme.themes.light.primary = colorUtil.rgbToHex(color);
    },
    updateSecondaryThemeColor(color) {
      this.$vuetify.theme.themes.light.secondary = {
        base: colorUtil.rgbToHex(color),
        lighten1: colorUtil.pSBC(0.6, colorUtil.rgbToHex(color)),
        lighten2: colorUtil.pSBC(0.76, colorUtil.rgbToHex(color)),
        lighten3: colorUtil.pSBC(0.93, colorUtil.rgbToHex(color)),
      };
    },
    updateTextThemeColor(color) {
      this.$vuetify.theme.themes.light.text = colorUtil.rgbToHex(color);
    },
    initGetProgressSteps() {
      outgoingPostMessageUtils.post('getChecklistProgress');
    },
    setFavicon() {
      let favicon = window.pov_config.VUE_APP_ENV || 'development';
      if (this.hasFeature('use_specific_favicon')) {
        favicon = this.hasFeature('star_favicon') ? 'star' : 'other';
      }
      faviconUtil.handleFavicon(favicon, this.hasFeature('enable_partner_favicon'));
    },
    handleQueryParams() {
      if (this.$route.query.sp_type && this.$route.query.sp_uid) {
        this.openSidePane({ type: this.$route.query.sp_type, uid: this.$route.query.sp_uid });
      }
      this.consumeAppMarketQuery();
    },
    // Under the POV market flag the ?appmarket= deep link is consumed on every route change and
    // removed from the URL, so Back and reload land on the plain page instead of reopening the market.
    async consumeAppMarketQuery() {
      const { appmarket, ...query } = this.$route.query;
      if (!appmarket || !this.hasFeature(APP_MARKET_FEATURE_FLAG) || !this.$hasFeature('app_market_available')) return;
      await this.$router.replace({ path: this.$route.path, query }).catch(() => undefined);
      this.openAppMarket(appmarket);
    },
    updateFullstoryWithStaffData() {
      const displayName = this.staffData.display_name;
      const { email } = this.staffData;
      this.setFullstoryData({ uid: this.staffUid, properties: { displayName, email } });
    },
    handlePaymentWizard(params) {
      communicationSdk('OpenModal', {
        modalName: 'PaymentWizard',
        modalProps: {
          navigateTo: params.navigateTo,
          source: params.source || 'campaign',
        },
      });
    },
    openXpertWizard() {
      communicationSdk('OpenModal', {
        modalName: 'XpertWizard',
      });
    },
    openAppMarket(appCodeName) {
      appMarketNavigationUtils.openAppMarket({ appCodeName });
    },
    async handleWizards() {
      const routeParams = await getInitialRouteQuery(this.$router);

      if (routeParams.wizard === 'ap') {
        this.handlePaymentWizard(routeParams);
      }
      if (routeParams.wizard === 'xw') {
        this.openXpertWizard();
      }
      if (routeParams.appmarket && this.$hasFeature('app_market_available') && !this.hasFeature(APP_MARKET_FEATURE_FLAG)) {
        this.openAppMarket(routeParams.appmarket);
      }

      // Migrated wizards from WizardsMessageListener
      if (routeParams.wizard === 'os') {
        communicationSdk('OpenModal', {
          modalName: 'OnlineScheduling',
        });
      }
      if (routeParams.wizard === 'ic') {
        communicationSdk('OpenModal', {
          modalName: 'ImportContacts',
        });
      }
      if (routeParams.wizard === 'cl') {
        communicationSdk('OpenModal', {
          modalName: 'OnboardingSelections',
          modalProps: [{
            skipToStepId: 'checklist',
          }],
        });
      }
      if (routeParams.wizard === 'pw') {
        communicationSdk('OpenModal', {
          modalName: 'OnboardingSelections',
          modalProps: [{
            skipToStepId: 'professionStep',
          }],
        });
      }
      if (routeParams.wizard === 'da') {
        communicationSdk('OpenModal', {
          modalName: 'DownloadAppWizard',
        });
      }
      if (routeParams.wizard === 'as') {
        communicationSdk('OpenModal', {
          modalName: 'OnlineScheduling',
          modalProps: [{
            wizard: 'staff',
          }],
        });
      }
      if (routeParams.wizard === 'mw') {
        communicationSdk('OpenModal', {
          modalName: 'MarketingWizard',
        });
      }
    },
    navigateToDefaultPage() {
      if (this.didNavigateToDefaultPage) {
        return;
      }
      // Check if the current path is the root path (no page rendered)
      const actualPath = window.location.pathname.replace('/app', '') || '/';
      if (actualPath && actualPath !== '/') {
        return;
      }
      if (this.defaultPage) {
        handleNavigation({ path: `/${this.defaultPage}` });
        this.didNavigateToDefaultPage = true;
      }
    },
  },
  watch: {
    primaryColor(color) {
      this.updatePrimaryThemeColor(color);
    },
    secondaryColor(color) {
      this.updateSecondaryThemeColor(color);
    },
    textColor(color) {
      this.updateTextThemeColor(color);
    },
    hasMobileNavigationRedesign() {
      this.syncPlatformMenuBodyClass();
    },
    isAngularLoaded() {
      this.initGetProgressSteps();
      this.syncMobileLayoutMetrics();
    },
    isRtl(isRtl) {
      this.$vuetify.rtl = isRtl;
    },
    staffDataLoaded(staffDataLoaded) {
      if (staffDataLoaded) {
        this.updateFullstoryWithStaffData();
      }
    },
    defaultPage() {
      if (this.defaultPage) {
        this.navigateToDefaultPage();
      }
    },
    $route() {
      this.handleQueryParams();
    },
    formattedDocumentTitle: {
      handler(newTitle, oldTitle) {
        if (newTitle && newTitle !== oldTitle) {
          document.title = newTitle;
        }
      },
      immediate: true,
    },
  },
};

</script>

<style lang="scss" scoped>
@import "@vcita/design-system/src/scss/mixins.scss";

$bottom_bar_height: 48px;
// Reserve the status-bar / notch inset above the top bar (0 on the native app and desktop).
$top_bar_height: calc(var(--vc-top-bar-height) + var(--safe-area-inset-top));

html {
  overflow: hidden;
}

html, body {
  height: 100%;
  @supports (height: 100dvh) {
    height: unset;
  }
}

#app {
  height: 100%;
  @supports (height: 100dvh) {
    height: 100dvh;
    ::v-deep .v-application--wrap {
      height: 100%;
      min-height: initial;
      overflow-y: hidden;
    }
  }

  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;

  .app-layout {
    position: relative;
    overflow: hidden;
    .pov-layout {
      text-align: center;
    }
  }
}

.blurred {
  filter: blur(5px);
}

.wide-menu-container {
  height: 100%;
}

.menu-container {

  &--mobile {
    z-index: 1;
  }
}

.content-area {
  container-name: content-area;
  container-type: size;
  position: relative;
  width: 100%;
  flex: 1 1 0;

  &.mobile-display {
    margin-bottom: calc(#{$bottom_bar_height} + var(--safe-area-inset-bottom));

    &.pov-content {
      margin-top: $top_bar_height;
    }

    &:not(.show-bottom-nav) {
      margin-bottom: unset;
    }
  }

  &.pov-content {
    overflow-y: auto;
  }

  &.mobile-display.mobile-platform-home-content {
    margin-top: 0;
  }

  &.show-bottom-nav {
    max-height: calc(100dvh - (#{$top_bar_height} + #{$bottom_bar_height} + var(--safe-area-inset-bottom)));
  }
}

.pointer-events-none {
  pointer-events: none;
}

.pointer-events-all {
  pointer-events: all;
}

.top-bar {
  position: absolute;
  width: 100%;

  &.desktop {
    position: relative;
  }
}

// Overlay below top bar so search bar stays clickable; overlay click closes search
.pov-overlay {
  z-index: 1 !important;
}

// New platform navigation layout - MasterTopBar at top with full width
.master-top-bar-container {
  width: 100%;
  flex-shrink: 0;
  position: relative;
  z-index: 2;
  justify-content: space-between;
}

.main-content-layout {
  flex: 1;
  min-height: 0;
  width: 100%;
}

.pov-content-container {
  display: flex;
  height: 100%;
  align-items: stretch;
  flex-direction: row-reverse;
  overflow-y: auto;
}

.content-column {
  flex: 1;
  min-width: 0;
}

.v-enter-active,
.v-leave-active {
  transition: opacity 0.5s ease;
}

.v-enter-from,
.v-leave-to {
  opacity: 0;
}

.embeddedAppContainer {
  position: absolute;
  height: calc(100% - #{$top_bar_height} - #{$bottom_bar_height} - var(--safe-area-inset-bottom));
  width: 100%;
  margin-bottom: calc(#{$bottom_bar_height} + var(--safe-area-inset-bottom));
  top: $top_bar_height;
  left: 0;

  &:not(.show-bottom-nav) {
    height: calc(100% - $top_bar_height);
    margin-bottom: 0;
  }

  @include md-and-up {
    height: 100%;
    width: calc(100% - 239px);
    left: 239px;
    margin-bottom: 0;
    top: $top_bar_height;
  }

  &.noAppDisplayed,
  &.mobileMenuDisplayed {
    pointer-events: none;
  }
}

.v-application.compact-view {
  .content-area.mobile-display {
    margin-bottom: 0;

    &.show-bottom-nav {
      max-height: calc(100dvh - $top_bar_height);
    }
  }

  .embeddedAppContainer {
    height: calc(100% - $top_bar_height);
    margin-bottom: 0;
  }
}

.rtl {
  font-family: var(--font-hebrew);
}

a {
  color: var(--link-color);
}
</style>

<style lang="scss">
body.bizai-help-hidden a[data-aichat] {
  display: none !important;
}
</style>
